import { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import type { CreativeItem } from "../../types/creatives.types";

interface ViewCreativeModalProps {
  open: boolean;
  onClose: () => void;
  creatives: CreativeItem[];
  initialIndex: number;
}

export function ViewCreativeModal({
  open,
  onClose,
  creatives,
  initialIndex,
}: ViewCreativeModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }, [open, initialIndex]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        handleNext();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, currentIndex]);

  if (!open || creatives.length === 0) return null;

  const currentCreative = creatives[currentIndex];
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < creatives.length - 1;

  const handlePrevious = () => {
    if (hasPrevious) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-w-4xl w-full mx-4 bg-white rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-600">
              {currentIndex + 1} de {creatives.length}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Image Container */}
        <div className="relative bg-gray-100 flex items-center justify-center">
          <div className="aspect-square max-h-[70vh] w-full flex items-center justify-center">
            <img
              src={currentCreative.image_url}
              alt={currentCreative.name || currentCreative.product_name}
              className="max-w-full max-h-full object-contain mx-auto"
            />
          </div>

          {/* Navigation Arrows */}
          {hasPrevious && (
            <button
              onClick={handlePrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
              aria-label="Anterior"
            >
              <ChevronLeft className="h-6 w-6 text-gray-700" />
            </button>
          )}
          {hasNext && (
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
              aria-label="Siguiente"
            >
              <ChevronRight className="h-6 w-6 text-gray-700" />
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {currentCreative.name || currentCreative.product_name || "Sin nombre"}
          </h3>
          <p className="text-sm text-gray-500 mt-1">Formato: {currentCreative.format}</p>
        </div>
      </div>
    </div>
  );
}

